/**
 * Shared client-search normalization.
 *
 * Every client search in the SPA used to do the same broken thing: glue the
 * name fields into one string and `includes()` the WHOLE query against it.
 * That requires the query to appear as a contiguous substring, in that exact
 * order, with identical accents — so typing "julio perez" found nobody even
 * though "julio" alone found him (Lissette 2026-07-21, agencia Maria Yepez):
 *
 *   - "Julio Perez" (with accent) vs "julio perez" -> accent mismatch
 *   - "Julio Cesar Perez"         vs "julio perez" -> words in between
 *   - "Perez, Julio"              vs "julio perez" -> wrong order
 *   - middleName wasn't even in the haystack
 *
 * The backend (deskte-api/src/utils/clientSearch.ts) already got this right —
 * AND across tokens, OR across fields — but the list screen never calls it:
 * the SPA hydrates the whole book and filters locally. These helpers make the
 * local filter behave like the server's.
 *
 * Keep in sync with deskte-api/src/utils/clientSearch.ts.
 */

// Combining diacritical marks — what NFD splits accents into. \p{M} is used
// instead of a literal U+0300-U+036F range so this source file stays pure
// ASCII (the Vite build emits ASCII; a raw range here has bitten us before).
const COMBINING_MARKS = /\p{M}/gu;

/**
 * Lowercase, strip accents (NFD + combining marks), drop punctuation.
 * "Perez-Gomez" -> "perezgomez", "(305) 555-0123" -> "3055550123".
 * Punctuation is removed rather than replaced by a space so hyphenated
 * surnames and formatted phone numbers still match either half.
 */
export function normalizeSearchText(s) {
  return String(s ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Split a raw query into normalized tokens. Empty query -> []. */
export function searchTokens(query) {
  const n = normalizeSearchText(query);
  return n ? n.split(' ').filter(Boolean) : [];
}

/**
 * True when EVERY token appears somewhere in the haystack — order-independent,
 * accent-insensitive. An empty token list matches everything (no filter).
 * `haystack` may be a string or an array of field strings.
 */
export function matchesAllTokens(haystack, tokens) {
  if (!tokens || tokens.length === 0) return true;
  const hay = Array.isArray(haystack)
    ? normalizeSearchText(haystack.filter(Boolean).join(' '))
    : normalizeSearchText(haystack);
  return tokens.every((t) => hay.includes(t));
}

/**
 * The standard searchable text for a client: the person, their spouse and
 * their dependents. Spouse lives NESTED under c.spouse; the flat
 * c.spouseFirstName fields are kept as a fallback for legacy rows (see the
 * 2026-06-30 "por spouse no sale" fix).
 */
export function clientSearchHaystack(c, extra = '') {
  if (!c) return '';
  const sp = c.spouse || {};
  const spouseName = `${sp.firstName || c.spouseFirstName || ''} ${sp.middleName || c.spouseMiddleName || ''} ${sp.lastName || c.spouseLastName || ''}`;
  const depNames = Array.isArray(c.dependents)
    ? c.dependents
        .map((d) => (d && typeof d === 'object' ? `${d.firstName || ''} ${d.middleName || ''} ${d.lastName || ''}` : ''))
        .join(' ')
    : '';
  return [
    c.firstName, c.middleName, c.lastName,
    c.email, c.phone,
    c.addressCity, c.addressState,
    spouseName, depNames, extra,
  ].filter(Boolean).join(' ');
}

/**
 * Filtro generico para un desplegable con buscador.
 *
 * `textoDe(item)` devuelve el texto por el que se busca ese item — para un
 * cliente es `clientSearchHaystack`, para una opcion "Nombre - Compania -
 * #Poliza" es su propia etiqueta. La comparacion es la misma de siempre: TODOS
 * los tokens, sin orden y sin acentos.
 *
 * `limite` recorta el resultado para no pintar miles de filas de golpe. Se
 * aplica DESPUES de filtrar, nunca antes: recortar primero y buscar despues
 * escondia clientes reales detras de un limite invisible.
 */
export function filtrarPorTexto(items, consulta, textoDe, limite = 300) {
  const tokens = searchTokens(consulta);
  const out = [];
  for (const it of (items || [])) {
    if (!matchesAllTokens(textoDe(it), tokens)) continue;
    out.push(it);
    if (limite > 0 && out.length >= limite) break;
  }
  return out;
}
